import React, { useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  message,
  Typography,
  Space,
  Alert,
  Divider,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useImportCargoFromExcelMutation, CargoImportRow } from '../../store/api/cargoApi';
import { API_BASE_URL } from '../../constants';
import type { UploadFile } from 'antd/es/upload/interface';
import { DataTable } from '../../ui';

const { Title } = Typography;

interface CargoImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (cargoData: CargoImportRow[]) => void;
}

const CargoImportModal: React.FC<CargoImportModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importResult, setImportResult] = useState<{
    data: CargoImportRow[];
    errors: string[];
    message: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const [importCargoFromExcel] = useImportCargoFromExcelMutation();

  const handleDownloadTemplate = async () => {
    try {
      const url = `${API_BASE_URL}/cargo/import-template`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          // allow browser to set content-type for binary
        }
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Download template failed:', response.status, text);
        message.error('Ошибка загрузки шаблона');
        return;
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'cargo_import_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      message.success('Шаблон загружен');
    } catch (error) {
      console.error('Ошибка скачивания шаблона:', error);
      message.error('Ошибка загрузки шаблона');
    }
  };

  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error('Выберите файл для импорта');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj as File);

      const result = await importCargoFromExcel(formData).unwrap();
      
      if (result.success) {
        setImportResult({
          data: result.data || [],
          errors: result.errors || [],
          message: result.message || 'Импорт завершен'
        });
        message.success(result.message || 'Файл успешно обработан');
      } else {
        message.error('Ошибка импорта файла');
      }
    } catch (error: any) {
      console.error('Ошибка импорта:', error);
      message.error(error?.data?.error || 'Ошибка импорта файла');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = () => {
    if (importResult?.data) {
      onSuccess(importResult.data);
      handleCancel();
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setImportResult(null);
    onCancel();
  };

  const columns = [
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      width: 150,
      render: (text: string) => text || '-'
    },
    {
      title: 'Количество',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
    },
    {
      title: 'Вес за единицу (кг)',
      dataIndex: 'weightPerItem',
      key: 'weightPerItem',
      width: 120,
      align: 'right' as const,
    },
    {
      title: 'Габариты (м)',
      key: 'dimensions',
      width: 120,
      render: (record: CargoImportRow) => {
        const { lengthM, widthM, heightM } = record;
        if (lengthM && widthM && heightM) {
          return `${lengthM}×${widthM}×${heightM}`;
        }
        return '-';
      }
    },
    {
      title: 'Тип упаковки',
      dataIndex: 'packagingType',
      key: 'packagingType',
      width: 120,
      render: (text: string) => text || '-'
    },
    {
      title: 'Характер груза',
      dataIndex: 'cargoCharacter',
      key: 'cargoCharacter',
      width: 120,
      render: (text: string) => text || '-'
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined />
          <span>Импорт грузов из Excel</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={1200}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Отмена
        </Button>,
        <Button key="template" icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
          Скачать шаблон
        </Button>,
        <Button 
          key="import" 
          type="primary" 
          icon={<UploadOutlined />}
          loading={loading}
          disabled={fileList.length === 0}
          onClick={handleImport}
        >
          Импортировать
        </Button>,
        importResult && (
          <Button 
            key="confirm" 
            type="primary" 
            icon={<CheckCircleOutlined />}
            onClick={handleConfirmImport}
          >
            Добавить в заявку
          </Button>
        )
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Инструкция по импорту"
          description={
            <div>
              <p>1. Скачайте шаблон Excel файла</p>
              <p>2. Заполните данные о грузах согласно шаблону</p>
              <p>3. Загрузите заполненный файл</p>
              <p>4. Проверьте результат и добавьте грузы в заявку</p>
            </div>
          }
          type="info"
          showIcon
        />
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Upload
            accept=".xlsx,.xls"
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList }) => setFileList(fileList)}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>
              Выберите Excel файл
            </Button>
          </Upload>
        </Col>
        <Col span={12}>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleDownloadTemplate}
          >
            Скачать шаблон
          </Button>
        </Col>
      </Row>

      {importResult && (
        <>
          <Divider />
          
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic
                title="Обработано строк"
                value={importResult.data.length}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Ошибок"
                value={importResult.errors.length}
                prefix={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Статус"
                value={importResult.errors.length === 0 ? "Успешно" : "С ошибками"}
                valueStyle={{ 
                  color: importResult.errors.length === 0 ? '#52c41a' : '#faad14' 
                }}
              />
            </Col>
          </Row>

          {importResult.errors.length > 0 && (
            <Alert
              message="Ошибки при обработке файла"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {importResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Title level={5}>Предварительный просмотр данных:</Title>
          <DataTable
            columns={columns}
            dataSource={importResult.data}
            rowKey={(record, index) => index?.toString() || '0'}
            pagination={false}
            scroll={{ y: 300 }}
            size="small"
          />
        </>
      )}
    </Modal>
  );
};

export default CargoImportModal;

